/**
 * MONSTER TOWN — PER-ROOM FLOOR & WALLPAPER STATE. OWNED BY THE INTERIORS CREW.
 *
 * Small on purpose. `src/content/furniture.ts` (the paint can and the floor
 * sample a kid taps) and `src/interior/house.ts` (the shell and the save) both
 * need this, and neither should have to import the other.
 *
 * Every function here is safe to call with a scene id that is not a room — the
 * LAW #1 audit runs interaction handlers against a sandbox scene, and a paint
 * can that throws there would fail the merge gate.
 */

import type { SceneId } from '../core/types'
import { FLOOR_STYLES, WALL_STYLES } from './style'

export interface RoomStyle {
  floor: number
  wall: number
}

const styles = new Map<string, RoomStyle>()
let listener: ((sceneId: SceneId) => void) | null = null

/** house.ts hooks the shell rebuild in here. */
export function onRoomStyleChanged(fn: ((sceneId: SceneId) => void) | null): void {
  listener = fn
}

export function hasRoomStyle(sceneId: SceneId): boolean {
  return styles.has(sceneId)
}

export function roomStyleOf(sceneId: SceneId): RoomStyle {
  return styles.get(sceneId) ?? { floor: 0, wall: 0 }
}

/** Seed or replace a room's style. Does NOT notify — used on load and setup. */
export function setRoomStyle(sceneId: SceneId, floor: number, wall: number): void {
  styles.set(sceneId, { floor, wall })
}

export function forgetRoomStyles(): void {
  styles.clear()
}

export function allRoomStyles(): [string, RoomStyle][] {
  return [...styles.entries()]
}

function bump(sceneId: SceneId, which: 'floor' | 'wall', span: number): number {
  const cur = styles.get(sceneId)
  if (cur === undefined) return -1
  const next = (cur[which] + 1) % span
  cur[which] = next
  listener?.(sceneId)
  return next
}

/** Tap the floor sample: the whole room's floor changes. Returns the new index. */
export function cycleRoomFloor(sceneId: SceneId): number {
  return bump(sceneId, 'floor', FLOOR_STYLES.length)
}

/** Tap the paint can: the whole room is repapered. Returns the new index. */
export function cycleRoomWall(sceneId: SceneId): number {
  return bump(sceneId, 'wall', WALL_STYLES.length)
}
